/**
 * ModelRegistry - 模型注册表
 *
 * 管理所有 3DGS 模型的注册、查找、注销。
 * URL 仅用于查询；同 URL 的模型实例和并发加载相互独立。
 */

import { getLoggerManager } from '@/shared/logging'
import type { GS3DModelInput, LODGroupEntry, SingleFileEntry } from './types'

const log = getLoggerManager().getLogger('gsplat')

/** 模型注册表 */
export class ModelRegistry {
  /** LOD 模型映射 (clientId -> entry) */
  readonly lodGroups: Map<string, LODGroupEntry> = new Map()

  /** 单文件模型映射 (clientId -> entry) */
  readonly singleFiles: Map<string, SingleFileEntry> = new Map()

  /** URL 到内部 ID 的映射 (url -> clientId) */
  readonly urlToClientId: Map<string, string> = new Map()

  /** 同 URL 的所有独立实例，按注册顺序保存。 */
  private readonly _urlToClientIds: Map<string, Set<string>> = new Map()

  /** 持久化 ID 到内部 ID 的映射 (nodeId -> clientId) */
  readonly nodeIdToClientId: Map<string, string> = new Map()

  /** 按 URL 统计独立的进行中加载数，不缓存或复用 Promise。 */
  private readonly _loadingCounts: Map<string, number> = new Map()

  // ==================== 注册 ====================

  /**
   * 注册 LOD 模型条目。
   *
   * @param entry - LOD 条目，需提前填好 clientId/url/nodeId
   */
  registerLOD(entry: LODGroupEntry): void {
    this.lodGroups.set(entry.clientId, entry)
    this._registerUrl(entry.url, entry.clientId)
    this.nodeIdToClientId.set(entry.nodeId, entry.clientId)
  }

  /**
   * 注册单文件模型条目。
   *
   * @param entry - 单文件条目，需提前填好 clientId/url/nodeId
   */
  registerSingle(entry: SingleFileEntry): void {
    this.singleFiles.set(entry.clientId, entry)
    this._registerUrl(entry.url, entry.clientId)
    this.nodeIdToClientId.set(entry.nodeId, entry.clientId)
  }

  // ==================== 注销 ====================

  /**
   * 注销 LOD 模型，仅清理映射表，不释放底层资源。
   *
   * @param clientId - LOD 条目 clientId
   * @returns 被注销的条目；不存在返回 undefined
   */
  unregisterLOD(clientId: string): LODGroupEntry | undefined {
    const entry = this.lodGroups.get(clientId)
    if (!entry) return undefined

    this.lodGroups.delete(clientId)
    this._unregisterUrl(entry.url, clientId)
    this.nodeIdToClientId.delete(entry.nodeId)
    return entry
  }

  /**
   * 注销单文件模型，仅清理映射表，不释放底层资源。
   *
   * @param clientId - 单文件条目 clientId
   * @returns 被注销的条目；不存在返回 undefined
   */
  unregisterSingle(clientId: string): SingleFileEntry | undefined {
    const entry = this.singleFiles.get(clientId)
    if (!entry) return undefined

    this.singleFiles.delete(clientId)
    this._unregisterUrl(entry.url, clientId)
    this.nodeIdToClientId.delete(entry.nodeId)
    return entry
  }

  // ==================== 查找 ====================

  /**
   * 通过 nodeId 解析 clientId。
   *
   * @param nodeId - 模型唯一标识
   * @returns clientId；未注册返回 undefined
   */
  resolveClientId(nodeId: string): string | undefined {
    return this.nodeIdToClientId.get(nodeId)
  }

  /**
   * 通过 nodeId 获取 LOD 条目。
   *
   * @param nodeId - 模型唯一标识
   * @returns LOD 条目；非 LOD 或未注册返回 undefined
   */
  getLODByNodeId(nodeId: string): LODGroupEntry | undefined {
    const clientId = this.nodeIdToClientId.get(nodeId)
    if (!clientId) return undefined
    return this.lodGroups.get(clientId)
  }

  /**
   * 通过 nodeId 获取单文件条目。
   *
   * @param nodeId - 模型唯一标识
   * @returns 单文件条目；非单文件或未注册返回 undefined
   */
  getSingleByNodeId(nodeId: string): SingleFileEntry | undefined {
    const clientId = this.nodeIdToClientId.get(nodeId)
    if (!clientId) return undefined
    return this.singleFiles.get(clientId)
  }

  /**
   * 根据 nodeId 获取模型。
   *
   * @param nodeId - 模型唯一标识
   * @returns LOD 条目、单文件 Mesh 或 undefined
   */
  getByNodeId(nodeId: string): unknown | undefined {
    const clientId = this.nodeIdToClientId.get(nodeId)
    if (!clientId) return undefined

    const lodEntry = this.lodGroups.get(clientId)
    if (lodEntry) return lodEntry

    const singleEntry = this.singleFiles.get(clientId)
    if (singleEntry) return singleEntry.mesh

    return undefined
  }

  /**
   * 根据 URL 获取模型。
   *
   * @param url - 加载时使用的 URL
   * @returns LOD 条目、单文件 Mesh 或 undefined
   */
  getByUrl(url: string): unknown | undefined {
    const clientId = this.getLatestClientIdByUrl(url)
    if (!clientId) return undefined

    const lodEntry = this.lodGroups.get(clientId)
    if (lodEntry) return lodEntry

    const singleEntry = this.singleFiles.get(clientId)
    if (singleEntry) return singleEntry.mesh

    return undefined
  }

  /**
   * 获取所有模型。
   *
   * @returns 按注册顺序排列的 LOD 条目和单文件 Mesh 混合数组
   */
  getAll(): unknown[] {
    const result: unknown[] = []
    for (const entry of this.lodGroups.values()) {
      result.push(entry)
    }
    for (const entry of this.singleFiles.values()) {
      result.push(entry.mesh)
    }
    return result
  }

  // ==================== 状态查询 ====================

  /**
   * 判断是否存在指定 nodeId 的模型。
   *
   * @param nodeId - 模型唯一标识
   * @returns 存在返回 true
   */
  hasByNodeId(nodeId: string): boolean {
    return this.nodeIdToClientId.has(nodeId)
  }

  /**
   * 判断是否已注册指定 URL 的模型。
   *
   * @param url - 加载时使用的 URL
   * @returns 存在返回 true
   */
  hasByUrl(url: string): boolean {
    return this.urlToClientId.has(url)
  }

  /**
   * 判断指定 URL 是否正在加载中。
   *
   * @param url - 加载时使用的 URL
   * @returns 正在加载返回 true
   */
  isLoading(url: string): boolean {
    return (this._loadingCounts.get(url) ?? 0) > 0
  }

  /** 记录一次独立加载开始。 */
  beginLoading(url: string): void {
    this._loadingCounts.set(url, (this._loadingCounts.get(url) ?? 0) + 1)
  }

  /** 记录一次独立加载结束。 */
  endLoading(url: string): void {
    const nextCount = (this._loadingCounts.get(url) ?? 0) - 1
    if (nextCount > 0) {
      this._loadingCounts.set(url, nextCount)
    } else {
      this._loadingCounts.delete(url)
    }
  }

  /** 获取指定 URL 最后注册的存活实例 clientId。 */
  getLatestClientIdByUrl(url: string): string | undefined {
    return this.urlToClientId.get(url)
  }

  private _registerUrl(url: string, clientId: string): void {
    let clientIds = this._urlToClientIds.get(url)
    if (!clientIds) {
      clientIds = new Set()
      this._urlToClientIds.set(url, clientIds)
    }
    clientIds.add(clientId)
    this.urlToClientId.set(url, clientId)
  }

  private _unregisterUrl(url: string, clientId: string): void {
    const clientIds = this._urlToClientIds.get(url)
    clientIds?.delete(clientId)
    if (!clientIds || clientIds.size === 0) {
      this._urlToClientIds.delete(url)
      this.urlToClientId.delete(url)
      return
    }

    const remainingClientIds = Array.from(clientIds)
    this.urlToClientId.set(url, remainingClientIds[remainingClientIds.length - 1])
  }

  /**
   * 获取所有已注册模型的 nodeId 列表。
   *
   * @returns nodeId 数组
   */
  getAllNodeIds(): string[] {
    return Array.from(this.nodeIdToClientId.keys())
  }

  // ==================== ID 管理 ====================

  /**
   * 设置节点 ID，更新模型的持久化 ID。
   *
   * @param group - 目标模型引用
   * @param nodeId - 新的 nodeId
   * @returns 绑定成功返回 true
   */
  setNodeId(group: GS3DModelInput, nodeId: string): boolean {
    let currentNodeId: string | undefined

    if (typeof group === 'object' && group !== null) {
      if ('id' in group && typeof (group as any).id === 'string') {
        currentNodeId = (group as any).id
      } else if ('nodeId' in group && typeof (group as any).nodeId === 'string') {
        currentNodeId = (group as any).nodeId
      } else {
        for (const [, entry] of this.singleFiles.entries()) {
          if (entry.mesh === group) {
            currentNodeId = entry.nodeId
            break
          }
        }
      }
    }

    if (!currentNodeId) {
      log.warn('[ModelRegistry] setNodeId: 无法从 group 对象中提取当前 nodeId')
      return false
    }

    const clientId = this.nodeIdToClientId.get(currentNodeId)
    if (!clientId) {
      log.warn(`[ModelRegistry] setNodeId: nodeId "${currentNodeId}" not found`)
      return false
    }

    if (this.nodeIdToClientId.has(nodeId) && this.nodeIdToClientId.get(nodeId) !== clientId) {
      log.warn(`[ModelRegistry] setNodeId: newNodeId "${nodeId}" already exists`)
      return false
    }

    // 更新 Entry 的 nodeId 字段
    const lodEntry = this.lodGroups.get(clientId)
    if (lodEntry) lodEntry.nodeId = nodeId

    const singleEntry = this.singleFiles.get(clientId)
    if (singleEntry) singleEntry.nodeId = nodeId

    // 更新映射
    this.nodeIdToClientId.delete(currentNodeId)
    this.nodeIdToClientId.set(nodeId, clientId)

    return true
  }

  /**
   * 从 GS3DModelInput 提取 nodeId。
   *
   * @param group - 目标模型引用
   * @returns 提取到的 nodeId；无法识别返回 undefined
   */
  extractNodeId(group: GS3DModelInput): string | undefined {
    if (!group || typeof group !== 'object') return undefined

    if ('nodeId' in group && typeof (group as any).nodeId === 'string') {
      return (group as any).nodeId
    }

    // Mesh 对象需要遍历查找
    for (const [, entry] of this.singleFiles.entries()) {
      if (entry.mesh === group) {
        return entry.nodeId
      }
    }

    return undefined
  }

  // ==================== 生命周期 ====================

  /** 清空所有映射 */
  clear(): void {
    this.lodGroups.clear()
    this.singleFiles.clear()
    this.urlToClientId.clear()
    this._urlToClientIds.clear()
    this.nodeIdToClientId.clear()
    this._loadingCounts.clear()
  }
}
